/**
 * Position constants — WI-15c refactor.
 *
 * After WI-15c:
 *   - POSITION_TAXONOMY (the static data map with slugs, display names, hierarchy)
 *     has been REMOVED. The DB is now the single source of truth for taxonomy data.
 *     Use fetchPositionTaxonomy(sportSlug) from api/positions.ts instead.
 *   - COARSE_TOOLTIP_COPY strings are RETAINED as frontend-only constants.
 *     Tooltip copy is product copy (tone/A-B reasons), not taxonomy data, and
 *     changes independently of DB migrations. Keeping it frontend-only means
 *     copy iterations do not require DB migrations or backend deploys.
 *     (resolved 2026-05-04: tooltip copy stays frontend-only, final for v1)
 *
 * Types (SportSlug, SportPositionTaxonomy) are unchanged in shape. They are still
 * the canonical interface consumed by components and api/positions.ts.
 *
 * CoarseSlug is retained for backward compat with callers that need the soccer
 * coarse slug type. It is scoped to soccer coarse slugs only (legacy).
 *
 * Backward-compat flat re-exports (COARSE_ORDER, FINE_UNDER_COARSE,
 * POSITION_DISPLAY_NAMES, POSITION_FULL_NAMES) have been REMOVED because all
 * consumers now read from the fetched taxonomy cache. COARSE_TOOLTIP_COPY is
 * retained as a flat re-export pointing at soccer_mens (legacy compat for
 * HierarchicalScoreGrid.test.tsx which imports COARSE_TOOLTIP_COPY directly).
 */

// ── Types ─────────────────────────────────────────────────────────────────────

export type SportSlug =
  | 'soccer_mens'
  | 'soccer_womens'
  | 'football_mens'
  | 'basketball_mens'
  | 'basketball_womens';

export interface SportPositionTaxonomy {
  COARSE_ORDER: readonly string[];
  FINE_UNDER_COARSE: Record<string, readonly string[]>;
  POSITION_DISPLAY_NAMES: Record<string, string>;
  POSITION_FULL_NAMES: Record<string, string>;
  /**
   * Tooltip copy per coarse slug.
   * Only coarse groups that have fine sub-positions need a tooltip. Keys that
   * are absent from this map will have no info icon rendered on the ScoreGrid card.
   * This field is populated by the frontend from SPORT_TOOLTIP_COPY (not fetched
   * from the backend — tooltip copy is product copy, not taxonomy data).
   */
  COARSE_TOOLTIP_COPY: Partial<Record<string, string>>;
}

/** Soccer coarse slugs (legacy type alias — retained for backward compat). */
export type CoarseSlug = 'gk' | 'def' | 'mid' | 'fwd';

// ── COARSE_TOOLTIP_COPY: sport-keyed map of tooltip copy strings ─────────────
//
// Tooltip copy is intentionally frontend-only (resolved 2026-05-04).
// Adding a new sport: add a new entry here keyed by its SportSlug.
// These strings are product copy — they change for tone/A-B/copy reasons,
// NOT for taxonomy structure reasons. No DB migration needed for copy changes.

export const SPORT_TOOLTIP_COPY: Record<SportSlug, Partial<Record<string, string>>> = {
  soccer_mens: {
    // WI-26 (AC-21): updated to acknowledge fallback behavior for fine positions.
    def: "Fine positions show scores for players explicitly labeled at that position. When no players are tagged at a fine position, the DEF score is shown as an estimate (~).",
    mid: "Fine positions show scores for players explicitly labeled at that position. When no players are tagged at a fine position, the MID score is shown as an estimate (~).",
    fwd: "Fine positions show scores for players explicitly labeled at that position. When no players are tagged at a fine position, the FWD score is shown as an estimate (~).",
  },
  soccer_womens: {
    def: "Fine positions show scores for players explicitly labeled at that position. When no players are tagged at a fine position, the DEF score is shown as an estimate (~).",
    mid: "Fine positions show scores for players explicitly labeled at that position. When no players are tagged at a fine position, the MID score is shown as an estimate (~).",
    fwd: "Fine positions show scores for players explicitly labeled at that position. When no players are tagged at a fine position, the FWD score is shown as an estimate (~).",
  },
  football_mens: {
    offense: "OFF score includes all offensive skill and line players on the roster, including players listed generically as 'Offense.' Fine positions (QB/RB/WR/TE/OL) only count players whose roster entry explicitly named that position.",
    defense: "DEF score includes all defensive players on the roster, including players listed generically as 'Defense.' Fine positions (DL/LB/CB/S) only count players whose roster entry explicitly named that position.",
    st:      "ST score includes kickers, punters, long snappers, and returners. Fine positions (K/P/LS/RET) only count players explicitly listed under that role. Note: K, P, and LS scores are highly volatile — a single player's graduation has an outsized effect.",
  },
  basketball_mens: {
    g: "G score includes all guards on the roster, including players listed generically as 'G.' Fine positions (PG/SG) only count players whose roster entry explicitly named that position.",
    f: "F score includes all frontcourt players on the roster (forwards and centers), including players listed generically as 'F.' Fine positions (SF/PF/C) only count players whose roster entry explicitly named that position.",
  },
  basketball_womens: {
    g: "G score includes all guards on the roster, including players listed generically as 'G.' Fine positions (PG/SG) only count players whose roster entry explicitly named that position.",
    f: "F score includes all frontcourt players on the roster (forwards and centers), including players listed generically as 'F.' Fine positions (SF/PF/C) only count players whose roster entry explicitly named that position.",
  },
};

// ── Backward-compat flat re-export ────────────────────────────────────────────
//
// COARSE_TOOLTIP_COPY: retained as a flat export for backward compat with
// HierarchicalScoreGrid.test.tsx (which imports COARSE_TOOLTIP_COPY directly).
// Points at soccer_mens tooltip copy (legacy behavior — tests are soccer-scoped).
// Remove once HierarchicalScoreGrid.test.tsx is updated to import SPORT_TOOLTIP_COPY.

export const COARSE_TOOLTIP_COPY: Partial<Record<string, string>> =
  SPORT_TOOLTIP_COPY.soccer_mens;
